import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, Calculator, Clock, AlertTriangle, MessageCircle } from "lucide-react";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import RelatedResources from "../components/RelatedResources";
import useJsonLd from "../hooks/useJsonLd";
import useCanonical from "../hooks/useCanonical";

const WHATSAPP_URL = "https://wa.me/918940988776?text=Hi%2C%20I%20need%20help%20with%20GST%20Return%20Filing%20in%20Coimbatore";

const FAQS = [
  { q: "What is the difference between GSTR-1 and GSTR-3B?", a: "GSTR-1 is the invoice-wise statement of all your outward supplies (sales) — filed by the 11th of the next month. GSTR-3B is the summary return where you actually pay the tax — filed by the 20th. Both must reconcile or the system flags a mismatch under Rule 88C." },
  { q: "Who can file GST returns quarterly under QRMP?", a: "Taxpayers with annual turnover up to ₹5 crore can opt for the QRMP (Quarterly Return, Monthly Payment) scheme. You file GSTR-1 and GSTR-3B quarterly but pay tax monthly through PMT-06 challans. B2B invoices can be uploaded month-wise via IFF." },
  { q: "What is the late fee for missing GSTR-3B?", a: "Late fee is ₹50 per day (₹20/day for nil returns), capped at ₹5,000 per return. Plus 18% annual interest on any outstanding tax. Continuous non-filing can block e-way bill generation and freeze marketplace payouts." },
  { q: "How do I claim Input Tax Credit (ITC)?", a: "ITC is claimed in GSTR-3B based on the auto-populated GSTR-2B statement. Since January 2026, the portal blocks GSTR-3B filing if your ITC claim exceeds GSTR-2B. We reconcile your purchase register with GSTR-2B to ensure you claim every eligible rupee." },
  { q: "What if I file GSTR-1 but miss GSTR-3B?", a: "GSTR-1 reports your sales, but GSTR-3B is where you pay tax. Filing only GSTR-1 means tax remains unpaid — interest accrues at 18% per annum. Repeated mismatches between GSTR-1 and GSTR-3B trigger Rule 88C demand notices." },
  { q: "Can I revise a GST return after filing?", a: "Direct revisions are not allowed. Errors must be corrected in the next month's GSTR-1 or GSTR-3B through amendment tables. Significant errors may require filing GSTR-9 (annual return) with reconciliations." },
  { q: "Do I need to file nil returns when there are no sales?", a: "Yes. Even with zero sales and zero purchases, you must file nil GSTR-1 and nil GSTR-3B every month. Skipping nil returns blocks the next month's filing and attracts late fee of ₹20/day." },
];

const RELATED = [
  { to: "/gst-registration", label: "GST Registration", description: "Get your GSTIN in 3–7 days", tag: "Service" },
  { to: "/ecom-gst-filing", label: "E-Commerce GST Filing", description: "TCS reconciliation for marketplace sellers", tag: "Service" },
  { to: "/gst-penalty-calculator", label: "GST Late Fee Calculator", description: "Calculate late fee + 18% interest", tag: "Free Tool" },
  { to: "/gst-interest-calculator", label: "GST Interest Calculator", description: "Section 50 interest on delayed payment", tag: "Free Tool" },
  { to: "/gst-reconciliation-worksheet", label: "GST Reconciliation Worksheet", description: "Match GSTR-1 with GSTR-2A before filing", tag: "Template" },
  { to: "/gst-notice-response-template", label: "GST Notice Response Template", description: "Reply to GST notices within deadline", tag: "Template" },
];

const GSTReturnFilingPage = () => {
  useCanonical("/gst-return-filing");
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "GST Return Filing in Coimbatore",
    "provider": { "@type": "LocalBusiness", "name": "WINTRUST SOLUTIONS" },
    "areaServed": { "@type": "City", "name": "Coimbatore" },
    "description": "Timely and accurate GST return filing services including GSTR-1, GSTR-3B, and annual returns."
  });
  useJsonLd({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  });

  return (
    <>
      <Helmet>
        <title>GST Return Filing in Coimbatore | Timely & Accurate | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Expert GST return filing services in Coimbatore. GSTR-1, GSTR-3B, annual returns. Zero penalties, on-time filing guaranteed. Contact WINTRUST SOLUTIONS today!" />
      </Helmet>
      {/* Hero */}
      <section className="bg-navy py-16">
        <div className="container-custom">
          <div className="max-w-3xl">
            <nav className="text-slate-400 text-sm mb-4">
              <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">GST Return Filing</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading">
              GST Return Filing Services in Coimbatore | Accurate & Timely
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Staying compliant with GST regulations requires timely and accurate filing of your returns. At WINTRUST SOLUTIONS, we offer comprehensive GST return filing services for businesses in Coimbatore, ensuring you never miss a deadline and avoid costly penalties.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact" className="btn-primary">Get a Quote</a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-whatsapp">
                <MessageCircle size={18} /> WhatsApp Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Return Types */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Monthly, Quarterly, and Annual GST Return Filing
          </h2>
          <p className="text-slate-600 mb-8">We handle all types of GST returns, including:</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { title: "GSTR-1", desc: "Statement of outward supplies - filed monthly/quarterly" },
              { title: "GSTR-3B", desc: "Monthly summary return with tax payment details" },
              { title: "GSTR-9", desc: "Annual return - comprehensive yearly filing" },
              { title: "Composition Returns", desc: "For eligible small taxpayers under composition scheme" },
            ].map((item, index) => (
              <div key={index} className="p-6 bg-slate-50 rounded-lg border-l-4 border-gold">
                <h3 className="font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Timely Filing */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Why Timely GST Filing Matters
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: <AlertTriangle size={32} />, title: "Avoid Penalties", desc: "Late filing attracts a penalty of ₹50 per day (₹20 for nil returns) and interest at 18% per annum on the outstanding tax amount." },
              { icon: <CheckCircle size={32} />, title: "Maintain Compliance Score", desc: "A good compliance score is crucial for your business's reputation and eligibility for various government schemes." },
              { icon: <Calculator size={32} />, title: "Seamless Input Tax Credit", desc: "Timely filing ensures that your customers can claim ITC without any issues, maintaining healthy business relationships." },
            ].map((item, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-lg shadow-sm">
                <div className="text-gold mb-4 flex justify-center">{item.icon}</div>
                <h3 className="font-semibold text-navy mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-semibold text-navy mb-8 font-heading">
            Our GST Return Filing Services Include
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Preparation and filing of GSTR-1, GSTR-3B, GSTR-9",
              "Reconciliation of purchase and sales data",
              "ITC reconciliation with GSTR-2A/2B",
              "Handling of GST notices and queries",
              "Amendment and correction of returns",
              "Zero/Nil return filing",
              "Annual return preparation and filing",
              "GST audit support (GSTR-9C)",
            ].map((service, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg">
                <CheckCircle className="text-gold flex-shrink-0" size={20} />
                <span className="text-navy">{service}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gold py-12">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-semibold text-navy mb-4 font-heading">
            Ready for Hassle-Free GST Return Filing?
          </h2>
          <p className="text-navy/80 mb-6 max-w-2xl mx-auto">
            Focus on growing your business and leave the complexities of GST filing to us. Contact WINTRUST SOLUTIONS for reliable and affordable GST return filing services.
          </p>
          <a href="#contact" className="bg-navy text-white px-8 py-3 rounded-md font-semibold hover:bg-navy-900 transition-colors">
            Get a Quote
          </a>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection items={FAQS} testId="gst-fil-faq" />

      {/* Related Resources */}
      <RelatedResources
        title="Related Tools & Services"
        subtitle="Stay compliant with these GST resources"
        items={RELATED}
        testId="gst-fil-related"
      />

      <ContactForm />
    </>
  );
};

export default GSTReturnFilingPage;
